import { getFragment } from '../../graphql';
import type {
    ClaimGeneratedPrescriberOrderBasicInfoFragment,
    ClaimGeneratedPrescriberOrderInfoFragment,
    DigitallyReceivedPrescriberOrderBasicInfoFragment,
    DigitallyReceivedPrescriberOrderInfoFragment,
    PriorAuthorizationInfoFragment,
    UnclaimedPrescriberOrderInfoFragment,
} from '../../graphql/generated/graphql';
import type { Prescription } from '../../routes/cabinet/components/HistorySearch';

export type PrescriberOrderFragment =
    | DigitallyReceivedPrescriberOrderInfoFragment
    | ClaimGeneratedPrescriberOrderInfoFragment
    | UnclaimedPrescriberOrderInfoFragment;

// Omit fragment name so PrescriberOrderFragment *can* be assigned to a basic fragment component
export type BasicPrescriberOrderFragment =
    | Omit<DigitallyReceivedPrescriberOrderBasicInfoFragment, ' $fragmentName'>
    | Omit<ClaimGeneratedPrescriberOrderBasicInfoFragment, ' $fragmentName'>
    | UnclaimedPrescriberOrderInfoFragment;

export const isDigitalPrescriberOrder = (
    order:
        | DigitallyReceivedPrescriberOrderBasicInfoFragment
        | ClaimGeneratedPrescriberOrderBasicInfoFragment
        | undefined
): order is DigitallyReceivedPrescriberOrderBasicInfoFragment => {
    return order?.__typename === 'DigitallyReceivedPrescriberOrder';
};

export const isClaimPrescriberOrder = (
    order:
        | DigitallyReceivedPrescriberOrderBasicInfoFragment
        | ClaimGeneratedPrescriberOrderBasicInfoFragment
        | undefined
): order is ClaimGeneratedPrescriberOrderBasicInfoFragment => {
    return order?.__typename === 'ClaimGeneratedPrescriberOrder';
};

export const isUnclaimedPrescriberOrder = (
    order:
        | BasicPrescriberOrderFragment
        | UnclaimedPrescriberOrderInfoFragment
        | undefined
): order is UnclaimedPrescriberOrderInfoFragment => {
    return order?.__typename === 'UnclaimedPrescriberOrder';
};

export const getMedicationDescription = (
    medication: BasicPrescriberOrderFragment | PriorAuthorizationInfoFragment
) => {
    switch (medication.__typename) {
        case 'ClaimGeneratedPrescriberOrder':
            return medication.productDescriptionClaimGenerated;
        case 'DigitallyReceivedPrescriberOrder':
            return medication.productDescription;
        case 'UnclaimedPrescriberOrder':
            return medication.productDescriptionUnclaimed;
        case 'PriorAuthorization':
            return getFragment(medication.medication).name;
        default:
            return undefined;
    }
};

export const getPrescribedProductFragment = (
    medication: BasicPrescriberOrderFragment | PriorAuthorizationInfoFragment
) => {
    switch (medication.__typename) {
        case 'ClaimGeneratedPrescriberOrder':
            return medication.prescribedProductClaimGenerated;
        case 'DigitallyReceivedPrescriberOrder':
            return medication.prescribedProduct;
        case 'UnclaimedPrescriberOrder':
            return medication.prescribedProductUnclaimed;
        case 'PriorAuthorization':
            return medication.medication;
        default:
            return undefined;
    }
};

export const getOrderQuantity = (
    medication: BasicPrescriberOrderFragment | PriorAuthorizationInfoFragment
) => {
    switch (medication.__typename) {
        case 'ClaimGeneratedPrescriberOrder':
            return medication.quantityClaimGenerated;
        case 'DigitallyReceivedPrescriberOrder':
            return medication.quantity;
        case 'UnclaimedPrescriberOrder':
            return getFragment(medication.prescribedProductUnclaimed)
                .modeQuantity;
        case 'PriorAuthorization':
            return medication.quantity;
        default:
            return undefined;
    }
};

export const getLatestActionDate = (
    orderFragment:
        | DigitallyReceivedPrescriberOrderInfoFragment
        | ClaimGeneratedPrescriberOrderInfoFragment
        | PriorAuthorizationInfoFragment
) => {
    switch (orderFragment.__typename) {
        case 'ClaimGeneratedPrescriberOrder':
            return orderFragment.writtenDateClaimGenerated;
        case 'DigitallyReceivedPrescriberOrder': {
            const priorAuth = getFragment(
                orderFragment.applicablePriorAuthorization
            );

            return (
                orderFragment.cancelDetails?.timestamp ??
                orderFragment.assignmentTimestamp ??
                orderFragment.latestSelectedOfferTimestamp ??
                priorAuth?.updatedAt
            );
        }

        case 'PriorAuthorization':
            return orderFragment.updatedAt;
        default:
            return undefined;
    }
};

/**
 * Converts a date input into a numeric timestamp format for consistent sorting (most recent to oldest)
 * @param date - string ('2025-06-25') | number (1726685810266) | null/undefined
 * @returns A timestamp or 0 if invalid input
 */
const normalizeDate = (date?: string | number | null): number => {
    if (!date) return 0;

    if (typeof date === 'number') {
        return date;
    }

    const parsed = Date.parse(date);
    return isNaN(parsed) ? 0 : parsed;
};

export const sortPrescriptionsByDate = (
    a: Prescription,
    b: Prescription
): number => {
    const dateA = normalizeDate(getLatestActionDate(a));
    const dateB = normalizeDate(getLatestActionDate(b));

    return dateB - dateA;
};
